import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { ContactDetail } from '../components/conversations/types';

// Fetches the full GHL contact for a conversation's contact_id. Shared by the
// context panel and the composer (channel capabilities, recipients, DND).
export function useContact(ghlId: string | null) {
  const [contact, setContact] = useState<ContactDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ⛔ I6 (CONV2's review): each load is numbered and only the LATEST may land. A lead the copy does not have is read
  // live from GoHighLevel (slower), so lead A's answer could arrive after lead B's - and the panel's tag editor writes to
  // the contact it SHOWS, while B's composer named A's phone as the recipient.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    if (!ghlId) {
      setContact(null);
      setLoading(false);
      return;
    }
    // The contact of the thread he left is not shown while the next one loads (a reload of the same one stays).
    setContact((shown) => (shown?.ghl_id === ghlId ? shown : null));
    setLoading(true);
    setError(null);
    try {
      const res = await api.getGhlContact(ghlId);
      if (seq !== loadSeq.current) return;
      setContact(res.contact || null);
    } catch (e: any) {
      if (seq !== loadSeq.current) return;
      setError(e?.message || 'Couldn’t load contact');
      setContact(null);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [ghlId]);

  useEffect(() => {
    load();
  }, [load]);

  return { contact, loading, error, reload: load };
}
